import React, {useState} from 'react'

import {H3, H5, Button, Alert} from '../../../../components/UI/index.js'
import {FormStack, Toggle, Input, Select} from '../../../../components/UI/forms/index.js'
import LoginActions from '../../../../actions/LoginActions.js'
import {Customer} from '../../../../../server/functions/customers/customers.types.js'

interface MagentoSettingsProps {
    user: Customer
    onChange: (user: Customer, save?: boolean) => void
}

export default function MagentoSettings({user, onChange}: MagentoSettingsProps) {
    const [loading, setLoading] = useState(false)
    const [verified, setVerified] = useState(false)
    const [error, setError] = useState('')

    const onChangeChecked = (key: string, event: {target: {checked: boolean}}) => {
        user.settings.webshops.magento[key] = event.target.checked

        onChange(user)
    }

    const onChangeValue = (key: string, event: React.ChangeEvent<HTMLInputElement>, parser?: (value: string) => number) => {
        user.settings.webshops.magento[key] = parser ? parser(event.target.value) : event.target.value

        onChange(user)
    }

    const onChangeValues = (key: string, event: any) => {
        user.settings.webshops.magento[key] = event.target.values

        onChange(user)
    }

    const onVerifyConnection = () => {
        setLoading(true)
        setVerified(false)
        setError('')

        onChange(user, true)

        setTimeout(() => { // wait for saving
            LoginActions.verifyWebshop('magento', (err) => {
                setLoading(false)
                if (err) {
                    setError(`Verificatie niet succesvol, controleer de ingevulde gegevens. Foutmelding: ${err}`)
                } else {
                    setVerified(true)
                }
            })
        }, 1000)
    }

    return (
        <div>
            <H3 style={{marginBottom: 36}}>Magento</H3>

            <H5 style={{marginBottom: 24}}>Instellingen</H5>

            <FormStack>
                <Toggle
                    label='Actief'
                    checked={user.settings.webshops.magento.active}
                    onChange={(event) => onChangeChecked('active', event)}
                />

                {user.settings.webshops.magento.active &&
                    <>
                        <Input
                            label='Webshop URL'
                            value={user.settings.webshops.magento.url}
                            onChange={(event) => onChangeValue('url', event)}
                        />

                        <Input
                            label='Access token'
                            value={user.settings.webshops.magento.accessToken}
                            onChange={(event) => onChangeValue('accessToken', event)}
                        />

                        <Input
                            type='number'
                            min={1}
                            max={180}
                            label='Aantal dagen terugkijken voor ophalen bestellingen'
                            value={user.settings.webshops.magento.nrOfDays || 7}
                            onChange={(event) => onChangeValue('nrOfDays', event, parseInt)}
                        />

                        <Select
                            label='Update bestelling status in Magento'
                            info=''
                            value={user.settings.webshops.magento.updateStatus}
                            onChange={(event) => onChangeValue('updateStatus', event)}
                        >
                            <option value=''>Nooit</option>
                            <option value='confirm'>Na bevestigen</option>
                            <option value='afterSent'>Na verzenden</option>
                        </Select>

                        <Select
                            label='Importeer bestellingen met status'
                            info='Selecteer de statussen waarvoor bestellingen worden geïmporteerd'
                            options={[
                                {value: 'pending', title: 'Pending'},
                                {value: 'processing', title: 'Processing'},
                                {value: 'complete', title: 'Complete'}
                            ]}
                            allowCustomValues
                            values={user.settings.webshops.magento.statuses || []}
                            onChange={(event) => onChangeValues('statuses', event)}
                        />

                        <Toggle
                            label='Apart veld voor huisnummer en toevoeging'
                            checked={user.settings.webshops.magento.separateHouseNrAndAdditionField}
                            onChange={(event) => onChangeChecked('separateHouseNrAndAdditionField', event)}
                            info='Zet deze optie aan als er in Magento gebruik wordt gemaakt van een apart veld voor huisnummer en toevoeging.'
                        />

                        <Toggle
                            label='Consument opmerking meegeven als bezorginstructie'
                            checked={user.settings.webshops.magento.useComment}
                            onChange={(event) => onChangeChecked('useComment', event)}
                        />

                        <Button
                            variant='text'
                            loading={loading}
                            onClick={onVerifyConnection}
                        >
                            Verifieer
                        </Button>

                        {error && (
                            <Alert style={{marginBottom: 6}} onClose={() => setError('')} variant='error'>{error}</Alert>
                        )}

                        {!error && verified && (
                            <Alert style={{marginBottom: 6}} onClose={() => setVerified(false)}>Verificatie succesvol.</Alert>
                        )}

                    </>
                }
            </FormStack>
        </div>
    )
}
